import { useCallback } from 'react';
import { useKeyboard } from '../../hooks/useKeyboard';

/**
 * KeyboardDismissView - Wrapper that dismisses keyboard on tap outside inputs
 *
 * Usage:
 * <KeyboardDismissView>
 *   <form>...</form>
 * </KeyboardDismissView>
 */
export default function KeyboardDismissView({
  children,
  className = '',
  disabled = false,
  onDismiss
}) {
  const { dismissKeyboard } = useKeyboard();

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;

    const target = e.target;

    // Don't dismiss if tapping on an input element or interactive element
    if (isInteractiveElement(target)) return;

    // Check if target is inside a form control
    if (target.closest('input, textarea, select, button, [role="button"], label')) return;

    // Dismiss keyboard
    dismissKeyboard();
    onDismiss?.();
  }, [disabled, dismissKeyboard, onDismiss]);

  return (
    <div
      className={className}
      onTouchStart={handleTouchStart}
      // Also handle mouse for web testing
      onMouseDown={(e) => {
        // Only on non-touch devices
        if ('ontouchstart' in window) return;
        handleTouchStart(e);
      }}
    >
      {children}
    </div>
  );
}

function isInteractiveElement(element) {
  const tagName = element.tagName.toLowerCase();
  const interactiveTags = ['input', 'textarea', 'select', 'button', 'a', 'label'];

  if (interactiveTags.includes(tagName)) return true;

  // Check for role="button" or tabindex
  if (element.getAttribute('role') === 'button') return true;
  if (element.hasAttribute('tabindex')) return true;

  // Check for contenteditable
  if (element.isContentEditable) return true;

  return false;
}
